import { useId } from "react";
import { cn } from "@/lib/utils";

/**
 * Background art for the brand's own surfaces: the corner of the page canvas,
 * the foot of the navy sidebar, and the login panel.
 *
 * One motif, from the business itself — waterless car care as water ripples,
 * a clean car as a few points of shine — drawn as concentric rings that echo
 * the round "O" of the logo. Colours are the brand tokens (cyan, sunflower,
 * and the sidebar's own ink), so the art re-tints with the theme and adds no
 * colour of its own.
 *
 * Rules that keep it from costing anything (admin-design, "Brand art"):
 * static, never animated; aria-hidden and pointer-events-none; always behind
 * content, and only where the canvas shows — the white cards above it are
 * opaque, so it never sits under data.
 */

/** A four-point sparkle: concave sides, the "shine" of a clean surface. */
function sparkle(x: number, y: number, r: number): string {
  return `M${x} ${y - r}Q${x} ${y} ${x + r} ${y}Q${x} ${y} ${x} ${y + r}Q${x} ${y} ${x - r} ${y}Q${x} ${y} ${x} ${y - r}Z`;
}

/** SVG ids must be unique on the page and may not contain React's colons. */
function useSvgId(): string {
  return useId().replace(/:/g, "");
}

/**
 * Ripples spreading from the top-right corner of the page, behind the page
 * title. Fades out well before the middle of the screen.
 *
 * No sparkles here: the corner is where a page's header actions sit, and a
 * point of shine peeking out beside a button reads as a glitch, not a flourish.
 * The sidebar and the login panel have room for them.
 */
export function CornerRipples({ className }: { className?: string }) {
  const id = useSvgId();
  const rings = [64, 112, 166, 226, 292, 364];
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 520 340"
      preserveAspectRatio="xMaxYMin meet"
      fill="none"
      className={cn("pointer-events-none select-none", className)}
    >
      <defs>
        <radialGradient
          id={`${id}-glow`}
          cx="520"
          cy="0"
          r="340"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="hsl(var(--brand-cyan))" stopOpacity="0.16" />
          <stop offset="1" stopColor="hsl(var(--brand-cyan))" stopOpacity="0" />
        </radialGradient>
        <radialGradient
          id={`${id}-fade`}
          cx="520"
          cy="0"
          r="420"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.45" stopColor="white" stopOpacity="1" />
          <stop offset="1" stopColor="white" stopOpacity="0" />
        </radialGradient>
        <mask id={`${id}-mask`}>
          <rect width="520" height="340" fill={`url(#${id}-fade)`} />
        </mask>
      </defs>
      <rect width="520" height="340" fill={`url(#${id}-glow)`} />
      <g mask={`url(#${id}-mask)`} stroke="hsl(var(--brand-cyan))" strokeWidth="1.25">
        {rings.map((r, index) => (
          <circle key={r} cx="520" cy="0" r={r} strokeOpacity={0.4 - index * 0.055} />
        ))}
      </g>
    </svg>
  );
}

/**
 * Rings rising from the bottom-left of the navy sidebar, in the sidebar's own
 * ink. Quiet enough to sit under the nav.
 *
 * No sparkle: on a short window the nav reaches down into the art, and a
 * point of shine beside a link ("Transcription words ✦") reads as a glitch.
 * Only the login panel has the room for shine.
 */
export function SidebarRipples({ className }: { className?: string }) {
  const id = useSvgId();
  const rings = [56, 100, 148, 200, 256];
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 240 280"
      preserveAspectRatio="xMinYMax slice"
      fill="none"
      className={cn("pointer-events-none select-none", className)}
    >
      <defs>
        <radialGradient
          id={`${id}-glow`}
          cx="0"
          cy="280"
          r="260"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="hsl(var(--brand-cyan))" stopOpacity="0.18" />
          <stop offset="1" stopColor="hsl(var(--brand-cyan))" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="240" height="280" fill={`url(#${id}-glow)`} />
      <g stroke="hsl(var(--sidebar-foreground))" strokeWidth="1">
        {rings.map((r, index) => (
          <circle key={r} cx="0" cy="280" r={r} strokeOpacity={0.12 - index * 0.018} />
        ))}
      </g>
    </svg>
  );
}

/**
 * The login panel's fuller version: ripples from the lower right, a swell of
 * water along the bottom, and shine — the one screen with room for it.
 */
export function LoginArt({ className }: { className?: string }) {
  const id = useSvgId();
  const rings = [90, 160, 236, 318, 406, 500];
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 800 900"
      preserveAspectRatio="xMidYMax slice"
      fill="none"
      className={cn("pointer-events-none select-none", className)}
    >
      <defs>
        <radialGradient
          id={`${id}-glow`}
          cx="760"
          cy="760"
          r="560"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="hsl(var(--brand-cyan))" stopOpacity="0.28" />
          <stop offset="1" stopColor="hsl(var(--brand-cyan))" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="800" height="900" fill={`url(#${id}-glow)`} />
      <g stroke="hsl(var(--brand-cyan))" strokeWidth="1.5">
        {rings.map((r, index) => (
          <circle key={r} cx="760" cy="760" r={r} strokeOpacity={0.42 - index * 0.06} />
        ))}
      </g>
      <path
        d="M0 760c130-50 260-50 390 0s260 50 410 0v140H0Z"
        fill="hsl(var(--brand-cyan))"
        fillOpacity="0.08"
      />
      <path
        d="M0 810c130-50 260-50 390 0s260 50 410 0v90H0Z"
        fill="hsl(var(--brand-cyan))"
        fillOpacity="0.07"
      />
      <path d={sparkle(560, 300, 14)} fill="hsl(var(--brand-sun))" />
      <path d={sparkle(640, 380, 7)} fill="hsl(var(--brand-cyan))" fillOpacity="0.8" />
      <path d={sparkle(480, 420, 5)} fill="hsl(var(--brand-cyan))" fillOpacity="0.55" />
    </svg>
  );
}
